import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { useState } from "react";
import { Button } from "@shared/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@shared/components/ui/form";
import { Input } from "@shared/components/ui/input";
import { Textarea } from "@shared/components/ui/textarea";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@shared/components/ui/avatar";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@shared/components/ui/card";
import { Separator } from "@shared/components/ui/separator";
import { useUpdateUser } from "./useUsers";
import { toast } from "sonner";
import { useAuth } from "@features/auth/contexts/AuthContext";
import { Camera, User, Mail, Phone, Globe, MapPin, Home } from "lucide-react";
import { useTranslation } from "react-i18next";
import { SelectCity } from "@features/settings/citys/SelectCity";
import { SelectQuartier } from "@features/settings/citys/SelectQuartier";

const formSchema = z.object({
  fullName: z.string().min(1, "Please provide a full name"),
  brandName: z.string().min(1, "Please provide a brand name"),
  email: z.string().email("Invalid email address").min(5).max(100),
  phone: z.string().min(1, "Please provide a phone number"),
  website: z.string().url("Invalid website URL").optional().or(z.literal("")),
  city: z.string().min(1, "Please provide a city"),
  address: z.string().min(1, "Please provide an address"),
  neighborhood: z.string().optional(),
});

export default function UserUpdateForm({ initailData }) {
  const { t } = useTranslation();
  const { updateUser, isLoading } = useUpdateUser();
  const auth = useAuth();

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: initailData || {
      fullName: "",
      brandName: "",
      email: "",
      phone: "",
      website: "",
      city: "",
      address: "",
      neighborhood: "",
    },
  });

  function onSubmit(values) {
    const toastId = toast.loading(t("messages.updating"));
    // Use the ID from initailData if available (for admin editing other users), otherwise use current user's ID
    const userId = initailData?._id || auth.user._id;
    updateUser(
      { id: userId, values },
      {
        onSuccess: () => {
          toast.success(t("messages.userUpdated"), {
            id: toastId,
          });
        },
        onError: (error) => {
          toast.error(`${error.response.data.message}`, {
            id: toastId,
          });
        },
      }
    );
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        {/* Personal Information Section */}
        <Card className="border-l-4 border-l-primary">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5 text-primary" />
              {t("users.personalInformation")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <FormField
                control={form.control}
                name="fullName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-2">
                      <User className="h-4 w-4" />
                      {t("users.fullName")}
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder={t("users.fullNamePlaceholder")}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="brandName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-2">
                      <User className="h-4 w-4" />
                      {t("users.brandName")}
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder={t("users.brandNamePlaceholder")}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </CardContent>
        </Card>

        <Separator className="my-6" />

        {/* Contact Information Section */}
        <Card className="border-l-4 border-l-blue-500">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2">
              <Phone className="h-5 w-5 text-blue-500" />
              {t("users.contactInformation")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-2">
                      <Mail className="h-4 w-4" />
                      {t("users.email")}
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        placeholder={t("users.emailPlaceholder")}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-2">
                      <Phone className="h-4 w-4" />
                      {t("users.phone")}
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder={t("users.phonePlaceholder")}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="website"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-2">
                      <Globe className="h-4 w-4" />
                      {t("users.website")}
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder={t("users.websitePlaceholder")}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </CardContent>
        </Card>

        <Separator className="my-6" />

        {/* Address Information Section */}
        <Card className="border-l-4 border-l-green-500">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5 text-green-500" />
              {t("users.addressInformation")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <FormField
                control={form.control}
                name="city"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-2">
                      <MapPin className="h-4 w-4" />
                      {t("users.city")}
                    </FormLabel>
                    <FormControl>
                      <SelectCity
                        city={field.value ? { name: field.value } : null}
                        onSelectCity={(selectedCity) => {
                          field.onChange(selectedCity?.name || "");
                          // Reset neighborhood when city changes
                          form.setValue("neighborhood", "");
                        }}
                        showCountry={true}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
                     <FormField
                control={form.control}
                name="neighborhood"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-2">
                      <MapPin className="h-4 w-4" />
                      {t("user.neighborhood")}
                    </FormLabel>
                    <FormControl>
                      <SelectQuartier
                        quartier={field.value}
                        onSelectQuartier={(quartier) => field.onChange(quartier)}
                        cityName={form.watch("city")}
                      />
                    </FormControl>
                    <FormDescription>
                      {form.watch("city")
                        ? t("users.selectNeighborhoodInCity")
                        : t("users.selectCityFirst")}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="address"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-2">
                      <Home className="h-4 w-4" />
                      {t("users.address")}
                    </FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder={t("users.addressPlaceholder")}
                        {...field}
                        rows={3}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
       
            </div>
          </CardContent>
        </Card>

        {/* Submit Button */}
        <div className="flex justify-end">
          <Button type="submit" disabled={isLoading} size="lg">
            {isLoading ? t("main.updating") : t("users.updateInformation")}
          </Button>
        </div>
      </form>
    </Form>
  );
}

export const UpdateImageProfile = ({ initailData }) => {
  const { t } = useTranslation();
  console.log(initailData);
  const { updateProfileImage } = useUpdateUser();
  const auth = useAuth();
  const form = useForm({
    defaultValues: {
      profileImage: null,
    },
  });

  const [previewImage, setPreviewImage] = useState(initailData || "");

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];

    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const onSubmit = async (values) => {
    const formData = new FormData();
    formData.append("profileImage", values.profileImage[0]);
    try {
      console.log("Form submitted:", values);
      updateProfileImage(
        { id: auth.user._id, values: formData },
        {
          onSuccess: () => {
            toast.success(t("messages.profileImageUpdated"));
          },
          onError: (error) => {
            toast.error(`${error.response.data.message}`);
          },
        }
      );
      // Handle your image upload logic here
    } catch (error) {
      console.error("Upload error:", error);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
        <FormField
          control={form.control}
          name="profileImage"
          id="profileImage"
          render={({ field: { onChange, ...field } }) => (
            <FormItem>
              <FormLabel>{t("users.profileImage")}</FormLabel>
              <FormControl>
                <div className="flex items-center space-x-8 rtl:space-x-reverse">
                  <div className="relative">
                    <Avatar className="w-20 h-20">
                      <AvatarImage src={previewImage} alt="Profile preview" />
                      <AvatarFallback>
                        <Camera className="w-8 h-8 text-gray-400" />
                      </AvatarFallback>
                    </Avatar>
                  </div>
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      onChange(e.target.files);
                      handleImageChange(e);
                    }}
                    className="max-w-xs"
                    name={field.name}
                    ref={field.ref}
                  />
                </div>
              </FormControl>
              <FormDescription>
                {t("users.profileImageDescription")}
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button type="submit">{t("users.updateProfileImage")}</Button>
      </form>
    </Form>
  );
};
